import React from 'react'
// libs
import styled from 'styled-components'
// components
import PlanDetails from 'components/PageComponents/Plans/Edit/components/ObjectivesListPreview/components/PlanDetails/index'
import SortableTree, { getFlatDataFromTree, getTreeFromFlatData } from 'react-sortable-tree'
// constants
import ObjectivesTreeTheme from 'components/PageComponents/Plans/Edit/components/ObjectivesListPreview/ObjectivesTreeTheme'
// context
import { ENTITIES, NODE_CONTENT_RENDERER_SIZES } from 'constants/index'
// themes
import { colors } from 'themes'
// lodash
import { get, flow, map, omit } from 'lodash'
import { filter, map as mapFp, orderBy, pipe } from 'lodash/fp'
// utils
import { applyTreeDeepLevels } from 'utils/objective'
import { getNodeKey, getKey, getParentKey, generatePath } from 'utils/tree'

const Wrapper = styled.div`
  background: ${colors.white100};
  display: flex;
  flex-direction: column;
  height: 100%;
`

const ObjectivesListPreview = ({ plan, planItems }) => {
  const entitiesMap = [
    { key: 'planObjective', omitKeys: ['planAction', 'planKPI'], type: ENTITIES.PLAN_OBJECTIVE },
    { key: 'planAction', omitKeys: ['planKPI', 'planObjective'], type: ENTITIES.PLAN_ACTION },
    { key: 'planKPI', omitKeys: ['planAction', 'planObjective'], type: ENTITIES.PLAN_KPI }
  ]

  const entities = entitiesMap.flatMap(({ key, omitKeys, type }) =>
    pipe(
      filter((item) => !!item[key]),
      mapFp((item) => ({
        ...omit(item, omitKeys),
        entity: item[key],
        entityId: item[key].id,
        entityType: type,
        path: generatePath(item[key].planItems.data.find(({ referenced }) => !referenced))
      }))
    )(planItems)
  )

  const levels = flow((item) => get(item, 'planType.levels', []), orderBy('position', 'asc'))(plan)

  const planTypeLevelIds = map('id', levels)

  // methods
  const generateNewNode = (treeItem) => ({
    ...treeItem,
    title: treeItem.entity.description,
    linkingItems: {
      sourceLinkingItems: treeItem.entity.sourceLinkingItems || [],
      destinationLinkingItems: treeItem.entity.destinationLinkingItems || []
    }
  })

  const prepareSource = (source) => {
    const orderedFlatTree = orderBy('treePosition', 'asc')(source)

    const tree = getTreeFromFlatData({
      flatData: orderedFlatTree,
      getKey,
      getParentKey,
      rootKey: null
    })

    applyTreeDeepLevels(tree, { levels, planTypeLevelIds })

    const flatTreeWithLevels = pipe(
      (array) =>
        getFlatDataFromTree({
          treeData: array,
          getNodeKey,
          ignoreCollapsed: false
        }),
      mapFp('node')
    )(tree)

    const flatTree = flatTreeWithLevels.map(generateNewNode)

    return getTreeFromFlatData({
      flatData: flatTree,
      getKey,
      getParentKey,
      rootKey: null
    })
  }

  return (
    <Wrapper>
      <PlanDetails plan={plan} />
      <SortableTree
        isVirtualized={false}
        getNodeKey={getNodeKey}
        theme={ObjectivesTreeTheme}
        treeData={prepareSource(entities)}
        rowHeight={NODE_CONTENT_RENDERER_SIZES.ROW_HEIGHT}
      />
    </Wrapper>
  )
}

export default ObjectivesListPreview
